import React, { useState } from "react";
import "../styles/Contact.css";
import axiosClient from "../api/axiosClient";
import { Helmet } from "react-helmet";
import toast, { Toaster } from "react-hot-toast";


const initialForm = {
  name: "",
  surname: "",
  email: "",
  phoneNumber: "",
  message: "",
};

const Contact = () => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});

  // ================= HANDLE CHANGE =================
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleLettersOnly = (field) => (e) => {
    const value = e.target.value;
    if (/^[A-Za-z ]*$/.test(value)) {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: "" }));
    } else {
      setErrors((prev) => ({ ...prev, [field]: "Only letters allowed" }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setFormData((prev) => ({ ...prev, phoneNumber: value }));
    setErrors((prev) => ({ ...prev, phoneNumber: "" }));
  };

  // ================= VALIDATION =================
  const validate = () => {
    const err = {};

    if (!formData.name.trim()) {
      err.name = "Name is required";
    } else if (!/^[A-Za-z ]+$/.test(formData.name)) {
      err.name = "Only letters allowed";
    }

    if (formData.surname && !/^[A-Za-z ]+$/.test(formData.surname)) {
      err.surname = "Only letters allowed";
    }

    if (!formData.email) {
      err.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      err.email = "Invalid email";
    }

    if (!formData.phoneNumber) {
      err.phoneNumber = "Phone is required";
    } else if (!/^\d{10,}$/.test(formData.phoneNumber)) {
      err.phoneNumber = "Enter a valid number";
    }

    if (!formData.message) {
      err.message = "Message required";
    }

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  // ================= SUBMIT =================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await axiosClient.post("/no-auth/api/users/saveContact", formData);
      toast.success("Message sent successfully");
      setFormData(initialForm);
      setErrors({});
      setOpen(false);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setOpen(false);
    setErrors({});
  };

  return (
    <div className="cmxl-contact-root">
      <Helmet>
        <title>Contact Codematrix | Get in Touch for Career Success</title>
        <meta
          name="description"
          content="Have questions about our AI skill assessments or pricing? Contact Codematrix today for expert support and start your journey toward a high-paying tech career."
        />
      </Helmet>

      <Toaster position="top-center" />

      <section className="cmxl-contact-section">
        <div className="cmxl-contact-container cmxl-contact-grid">
          {/* LEFT IMAGE */}
          <div className="cmxl-contact-image-wrap">
            <img src="/ContactPage.jpeg" alt="Codematrix support team ready to help" />
          </div>

          {/* RIGHT CONTENT */}
          <div>
            <span className="cmxl-contact-eyebrow">★ We&rsquo;re here to help</span>
            <h1 className="cmxl-contact-title">Get in Touch with Us!</h1>
            <p className="cmxl-contact-lede">
              Have questions or need assistance? We&rsquo;re here to guide you and support your journey.
            </p>

            <div className="cmxl-contact-info-list">
              <div className="cmxl-contact-card">
                <span className="cmxl-contact-card__icon">📞</span>
                <div>
                  <h4>+91 7302227370</h4>
                  <p>Speak directly with our support team for quick assistance and personalized guidance.</p>
                </div>
              </div>

              <div className="cmxl-contact-card">
                <span className="cmxl-contact-card__icon">✉️</span>
                <div>
                  <h4>HR@codematrix.co.in</h4>
                  <p>Drop us an email anytime and our team will respond within 24 hours.</p>
                </div>
              </div>

              <div className="cmxl-contact-card">
                <span className="cmxl-contact-card__icon">📍</span>
                <div>
                  <h4>Noida, Uttar Pradesh</h4>
                  <p>Visit our office for one-on-one consultation and career guidance.</p>
                  <ul className="cmxl-contact-card__points">
                    <li>H 140, Sector 63 Rd, H Block, Sector 63, Noida, Uttar Pradesh 201309</li>
                  </ul>
                </div>
              </div>
            </div>

            <button type="button" className="cmxl-contact-cta" onClick={() => setOpen(true)}>
              Contact Us
            </button>
          </div>
        </div>
      </section>

      {/* ================= POPUP ================= */}
      {open && (
        <div className="cmxl-contact-overlay" onClick={closeModal}>
          <div className="cmxl-contact-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="cmxl-contact-close" onClick={closeModal} aria-label="Close">
              ✕
            </button>
            <h3>Contact Form</h3>

            <form onSubmit={handleSubmit} noValidate>
              {/* NAME */}
              <input
                name="name"
                value={formData.name}
                maxLength={100}
                onChange={handleLettersOnly("name")}
                placeholder="Name"
                className={`cmxl-contact-field${errors.name ? " cmxl-contact-field--error" : ""}`}
              />
              {errors.name && <p className="cmxl-contact-error">{errors.name}</p>}

              {/* SURNAME */}
              <input
                name="surname"
                value={formData.surname}
                maxLength={100}
                onChange={handleLettersOnly("surname")}
                placeholder="Surname"
                className={`cmxl-contact-field${errors.surname ? " cmxl-contact-field--error" : ""}`}
              />
              {errors.surname && <p className="cmxl-contact-error">{errors.surname}</p>}

              {/* EMAIL */}
              <input
                name="email"
                type="email"
                value={formData.email}
                maxLength={100}
                onChange={handleChange}
                placeholder="Email"
                className={`cmxl-contact-field${errors.email ? " cmxl-contact-field--error" : ""}`}
              />
              {errors.email && <p className="cmxl-contact-error">{errors.email}</p>}

              {/* PHONE */}
              <input
                name="phoneNumber"
                value={formData.phoneNumber}
                maxLength={10}
                onChange={handlePhoneChange}
                placeholder="Phone Number"
                className={`cmxl-contact-field${errors.phoneNumber ? " cmxl-contact-field--error" : ""}`}
              />
              {errors.phoneNumber && <p className="cmxl-contact-error">{errors.phoneNumber}</p>}

              {/* MESSAGE */}
              <textarea
                name="message"
                value={formData.message}
                maxLength={500}
                onChange={handleChange}
                placeholder="Message..."
                className={`cmxl-contact-field${errors.message ? " cmxl-contact-field--error" : ""}`}
              />
              {errors.message && <p className="cmxl-contact-error">{errors.message}</p>}

              <button type="submit" className="cmxl-contact-submit" disabled={submitting}>
                {submitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contact;